import { Icon } from 'iconsax-react';
import React, { ForwardedRef, JSX, useMemo } from 'react';

import Spinner from '@/components/Spinner';

import classNames from '../../helpers/classNames';
import styles from './Button.module.scss';

export type ButtonVariant =
  | 'blue'
  | 'white'
  | 'gray'
  | 'redAlert'
  | 'whiteAlert'
  | 'tertiary'
  | 'linkBlack'
  | 'linkRed'
  | 'linkBlue'
  | 'dashed'
  | 'dashedGray'
  | 'ghost'; // new variant accoridng to rebranding

export interface ButtonProps
  extends Omit<React.ComponentPropsWithoutRef<'button'>, 'size'> {
  text?: string | JSX.Element;
  contentClassName?: string;
  variant?: ButtonVariant;
  size?: 'small' | 'medium' | 'main';
  LeftIcon?: Icon;
  RightIcon?: Icon | JSX.Element;
  /**
   * When set to true, rotates all icons 90deg
   */
  iconVertical?: boolean;
  /**
   * When set to true, rotates all icons 180deg
   */
  iconFlipped?: boolean;
  /**
   * When set to true, icon rotation is animated
   */
  animateIcon?: boolean;
  iconSize?: number;
  fullWidth?: boolean;
  isPending?: boolean;
  noUnderline?: boolean;
}

const ButtonInner = (
  props: ButtonProps,
  ref: ForwardedRef<HTMLButtonElement>
) => {
  const {
    text,
    className,
    contentClassName,
    variant = 'blue',
    size = 'main',
    LeftIcon,
    RightIcon,
    iconVertical = false,
    iconFlipped = false,
    animateIcon = false,
    disabled,
    fullWidth,
    iconSize,
    isPending,
    noUnderline,
    ...buttonProps
  } = props;

  const _iconSize = useMemo(() => {
    if (iconSize) {
      return iconSize;
    }

    switch (variant) {
      case 'tertiary':
        return 16;
      case 'dashed':
        return 32;
      default:
        return 20;
    }
  }, [iconSize, variant]);

  const spinnerColor = useMemo(() => {
    switch (variant) {
      case 'blue':
      case 'redAlert':
        return 'white';
      default:
        return 'blue';
    }
  }, [variant]);

  return (
    <button
      ref={ref}
      className={classNames(
        [styles.button, styles[variant], styles[size], className],
        { [styles.fullWidth]: fullWidth, [styles.noUnderline]: noUnderline }
      )}
      disabled={disabled}
      {...buttonProps}
    >
      <span className={classNames([styles.buttonContent, contentClassName])}>
        {LeftIcon ? (
          <div
            className={classNames([styles.leftIcon], {
              [styles.vertical]: iconVertical,
              [styles.expanded]: iconFlipped,
              [styles.animated]: animateIcon,
            })}
          >
            <LeftIcon variant="Linear" color="currentColor" size={_iconSize} />
          </div>
        ) : null}
        {text}
        {RightIcon ? (
          <div
            className={classNames([styles.rightIcon], {
              [styles.vertical]: iconVertical,
              [styles.expanded]: iconFlipped,
              [styles.animated]: animateIcon,
            })}
          >
            {React.isValidElement(RightIcon)
              ? RightIcon
              : (() => {
                  const IconComponent = RightIcon as Icon;
                  return (
                    <IconComponent
                      variant="Linear"
                      color="currentColor"
                      size={_iconSize}
                    />
                  );
                })()}
          </div>
        ) : null}
        {typeof isPending === 'boolean' && (
          <div
            className={classNames([styles.loader], {
              [styles.visible]: isPending,
            })}
          >
            <Spinner
              className={styles.spinner}
              size={16}
              color={spinnerColor}
            />
          </div>
        )}
      </span>
    </button>
  );
};

const Button = React.forwardRef(ButtonInner);
export default Button;
